import React from 'react';
import { ErrorMessage, Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import { observer } from 'mobx-react';
import DatePickerField from '@shared/fields/DatePickerField';
import Field from '../../fields/Field';
import media from '../../../utils/mediaQueries';
import { ERROR_COLOR } from '../../../theme';
import { Checkbox } from '../../fields/Checkbox';
import { CheckboxLabel } from './CheckboxLabel';
import Btn from '../../buttons/Btn';
import { Title } from './Title';
import stores from '../../../stores';
import { Input } from '../../fields/common/Input';
import { isMobile } from '../../../constants';
import { PasswordField } from '../../fields/PasswordField';

const Section = styled.section`
  margin-bottom: 40px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  letter-spacing: 0.3px;
  margin: 16px 0 8px;
  ${(props) => props.error && `color: ${ERROR_COLOR};`}
`;

const SmallInput = styled.div`
  width: 224px;

  ${media.mobile`
    width: 100%;
  `}
`;

const ErrorMessageContainer = styled.div`
  color: ${ERROR_COLOR};
  font-size: 12px;
  line-height: 1.17;
  margin-top: 3px;
`;

const CheckboxRow = styled.div`
  margin-top: 16px;
`;

const BtnContainer = styled.div`
  margin-top: 27px;
  max-width: 384px;
`;

const formInitialValues = {
  prenom: '',
  nom: '',
  dateNaissance: isMobile ? '' : undefined,
  phone: '',
  email: '',
  password: '',
  isAgreed: false,
};

const validationSchema = Yup.object().shape({
  prenom: Yup.string().required('Ce champ est obligatoire.'),
  nom: Yup.string().required('Ce champ est obligatoire.'),
  dateNaissance: Yup.date()
    .required('Ce champ est obligatoire.')
    .max(new Date(), 'La date ne peut pas être dans le futur')
    .typeError('Ce champ doit être une date (jj/mm/aaaa).'),
  phone: Yup.string()
    .matches(
      /^(?:(?:)0)\s*[1-9](?:\s*\d{2}){4}$/,
      'Le téléphone doit être au format 01 23 45 67 89'
    )
    .required('Ce champ est obligatoire.'),
  email: Yup.string()
    .trim("Ce champ ne peut pas inclure d'espace au début et à la fin")
    .strict(true)
    .email('Veuillez fournir une adresse électronique valide.')
    .required('Ce champ est obligatoire.'),
  password: Yup.string().required('Ce champ est obligatoire.'),
  isAgreed: Yup.bool().oneOf([true], 'Veuillez accepter les Conditions générales.'),
});

const LoginAndPayForm = observer(({ togglePaymentForm, cost, schoolId }) => {
  const onSubmit = async (data, formikBag) => {
    const dataToSend = {
      ...data,
      dateNaissance: format(new Date(data.dateNaissance), 'yyyy-MM-dd'),
    };
    try {
      await stores.userStore.signUpTheoryAccessStudent(schoolId, dataToSend, formikBag);
      togglePaymentForm();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const { isPaymentDataLoading } = stores.theoryAccessStore;

  return (
    <Formik
      initialValues={formInitialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({
        values,
        handleSubmit,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit}>
          <Section>
            <Title>Mes coordonnées</Title>
            <Label error={touched.prenom && errors.prenom}>Prénom</Label>
            <Field
              value={values.prenom}
              name="prenom"
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Louis"
              valid={touched.prenom && !errors.prenom}
            />
            <ErrorMessage component={ErrorMessageContainer} name="prenom" />

            <Label error={touched.nom && errors.nom}>Nom</Label>
            <Field
              value={values.nom}
              name="nom"
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Dupont"
              valid={touched.nom && !errors.nom}
            />
            <ErrorMessage component={ErrorMessageContainer} name="nom" />

            <Label error={touched.dateNaissance && errors.dateNaissance}>Date de naissance</Label>
            <SmallInput>
              {isMobile ? (
                <Input
                  name="dateNaissance"
                  value={values.dateNaissance}
                  width="100%"
                  type="date"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="jj/mm/aaaa"
                  valid={touched.dateNaissance && !errors.dateNaissance}
                />
              ) : (
                <DatePickerField
                  name="dateNaissance"
                  placeholderText="jj/mm/aaaa"
                  value={values.dateNaissance}
                  maxDate={new Date()} // today
                  onChange={setFieldValue}
                  onBlur={handleBlur}
                  dateFormat="dd/MM/yyyy"
                  valid={touched.dateNaissance && !errors.dateNaissance}
                />
              )}
            </SmallInput>
            <ErrorMessage component={ErrorMessageContainer} name="dateNaissance" />

            <Label error={touched.phone && errors.phone}>Téléphone</Label>
            <SmallInput>
              <Field
                value={values.phone}
                name="phone"
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="0601020304"
                valid={touched.phone && !errors.phone}
              />
            </SmallInput>
            <ErrorMessage component={ErrorMessageContainer} name="phone" />
          </Section>

          <Section>
            <Title>Mes identifiants</Title>
            <Label error={touched.email && errors.email}>Email</Label>
            <Field
              value={values.email}
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              valid={touched.email && !errors.email}
            />
            <ErrorMessage component={ErrorMessageContainer} name="email" />
            <PasswordField
              name="password"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              valid={touched.password && !errors.password}
              errorMessage={errors.password && touched.password ? errors.password : null}
            />
            <CheckboxRow>
              <Checkbox
                name="isAgreed"
                label={<CheckboxLabel />}
                isChecked={values.isAgreed}
                onChange={handleChange}
                error={errors.isAgreed && touched.isAgreed}
              />
            </CheckboxRow>
            <ErrorMessage component={ErrorMessageContainer} name="isAgreed" />
            <BtnContainer>
              <Btn
                type="submit"
                text={`Paiement (${cost}€)`}
                name="payBtn"
                isLoading={isSubmitting || isPaymentDataLoading}
                disabled={isSubmitting || isPaymentDataLoading}
                block
              />
            </BtnContainer>
          </Section>
        </form>
      )}
    </Formik>
  );
});

LoginAndPayForm.propTypes = {
  togglePaymentForm: PropTypes.func.isRequired,
  cost: PropTypes.string.isRequired,
};

export { LoginAndPayForm };
